import { Helmet } from 'react-helmet-async';
import { useMemo, useState } from 'react';
import {
  Box,
  Container,
  Card,
  Grid,
  Typography,
  TextField,
  InputAdornment,
  alpha,
  styled
} from '@mui/material';
import { SearchTwoTone, MenuBookTwoTone } from '@mui/icons-material';
import PageTitleWrapper from '@/components/PageTitleWrapper';
import { tokenDecoder } from '@/utils/jwt';
import { Role } from '@/enums/role';
import { DOCS, DocSection } from '@/constants/docs';
import DocSidebar from './DocSidebar';
import DocArticleView from './DocArticleView';

/* ─── Styled ─── */

const HeroCard = styled(Card)(
  ({ theme }) => `
    background: linear-gradient(135deg, ${theme.colors.primary.main} 0%, ${alpha(
      theme.colors.primary.dark,
      0.85
    )} 100%);
    border-radius: ${theme.general.borderRadiusXl};
    color: ${theme.palette.primary.contrastText};
    padding: ${theme.spacing(3, 3.5)};
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: -50%;
      right: -10%;
      width: 360px;
      height: 360px;
      border-radius: 50%;
      background: ${alpha('#fff', 0.05)};
    }
  `
);

const NavCard = styled(Card)(
  ({ theme }) => `
    position: sticky;
    top: ${theme.spacing(2)};
    max-height: calc(100vh - ${theme.spacing(12)});
    overflow-y: auto;
  `
);

/** Rolüne göre bir makalenin görünüp görünmeyeceğini belirler. */
function filterByRole(sections: DocSection[], role: Role | null): DocSection[] {
  return sections
    .map((section) => ({
      ...section,
      articles: section.articles.filter((a) => !role || a.roles.includes(role))
    }))
    .filter((section) => section.articles.length > 0);
}

/** Arama terimine göre makaleleri süzer (başlık + özet + adım metinleri). */
function filterBySearch(sections: DocSection[], term: string): DocSection[] {
  const q = term.trim().toLocaleLowerCase('tr');
  if (!q) return sections;
  return sections
    .map((section) => ({
      ...section,
      articles: section.articles.filter((a) => {
        const haystack = [
          a.title,
          a.summary,
          ...a.steps.map((s) => s.text),
          ...(a.tips || [])
        ]
          .join(' ')
          .toLocaleLowerCase('tr');
        return haystack.includes(q);
      })
    }))
    .filter((section) => section.articles.length > 0);
}

function DocsPage() {
  // Rol: JWT içinden (SidebarMenu ile aynı yöntem)
  const role = useMemo<Role | null>(() => {
    try {
      const token = localStorage.getItem('access_token');
      const decoded = token ? tokenDecoder(token) : null;
      return (decoded?.role as Role) || null;
    } catch {
      return null;
    }
  }, []);

  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const roleFiltered = useMemo(() => filterByRole(DOCS, role), [role]);
  const visibleSections = useMemo(
    () => filterBySearch(roleFiltered, search),
    [roleFiltered, search]
  );

  // Seçili makale — yoksa görünen ilk makale
  const allVisibleArticles = visibleSections.flatMap((s) => s.articles);
  const activeArticle =
    allVisibleArticles.find((a) => a.id === selectedId) ||
    allVisibleArticles[0] ||
    null;

  return (
    <>
      <Helmet>
        <title>Yardım / Kullanım Kılavuzu</title>
      </Helmet>
      <PageTitleWrapper>
        <Box display="flex" alignItems="center" gap={1.5}>
          <MenuBookTwoTone color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h3" component="h3" gutterBottom>
              Yardım / Kullanım Kılavuzu
            </Typography>
            <Typography variant="subtitle2">
              Panelin tüm işlevlerini adım adım öğrenin. Her konuya ait eğitim
              videosu ilgili anlatımın üstündedir.
            </Typography>
          </Box>
        </Box>
      </PageTitleWrapper>

      <Container disableGutters maxWidth={false} sx={{ maxWidth: '92%' }}>
        <Grid container spacing={3}>
          {/* Hero + arama */}
          <Grid item xs={12}>
            <HeroCard>
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
                  Size nasıl yardımcı olabiliriz?
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
                  Aradığınız konuyu yazın veya soldaki başlıklardan seçin.
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Konu ara… (örn. şifre, rapor, zayi)"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{
                    maxWidth: 520,
                    bgcolor: 'background.paper',
                    borderRadius: 1
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchTwoTone />
                      </InputAdornment>
                    )
                  }}
                />
              </Box>
            </HeroCard>
          </Grid>

          {/* Sol navigasyon */}
          <Grid item xs={12} md={4} lg={3}>
            <NavCard>
              <DocSidebar
                sections={visibleSections}
                selectedId={activeArticle?.id || null}
                onSelect={setSelectedId}
              />
            </NavCard>
          </Grid>

          {/* Makale içeriği */}
          <Grid item xs={12} md={8} lg={9}>
            {activeArticle ? (
              <DocArticleView article={activeArticle} />
            ) : (
              <Card sx={{ p: 6, textAlign: 'center' }}>
                <MenuBookTwoTone sx={{ fontSize: 56, opacity: 0.35 }} />
                <Typography variant="h5" sx={{ mt: 2, fontWeight: 700 }}>
                  Görüntülenecek konu bulunamadı
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Arama teriminizi değiştirin veya temizleyin.
                </Typography>
              </Card>
            )}
          </Grid>
        </Grid>
      </Container>
    </>
  );
}

export default DocsPage;
